import { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import EmployeeFilters from "./components/EmployeeFilters";
import EmployeeTable from "./components/EmployeeTable";
import Pagination from "./components/Pagination";

const ITEMS_PER_PAGE = 10;

const displayStatus = {
  ACTIVE: "Active",
  DRAFT: "Draft",
  INACTIVE: "Inactive",
  EXITED: "Exited",
};

export default function EmployeeList() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [department, setDepartment] = useState("All");
  const [branch, setBranch] = useState("All");
  const [status, setStatus] = useState("All");
  const [bankDetails, setBankDetails] = useState("All");
  const [identityDetails, setIdentityDetails] = useState("All");
  const [systemAccess, setSystemAccess] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [inactiveFile, setInactiveFile] = useState(null);
  const [inactivePreview, setInactivePreview] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const importInactive = async (file, confirm = false) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const result = await apiRequest('/employees/imports/inactive/' + (confirm ? 'confirm' : 'preview'), { method: 'POST', body, accessToken });
      if (confirm) { setInactivePreview(null); setInactiveFile(null); await loadEmployees(); }
      else { setInactiveFile(file); setInactivePreview(result); }
    } catch (error) { setLoadError(error.message); }
    finally { setImportBusy(false); }
  };

  const loadEmployees = useCallback(async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setLoadError("");

      const response = await apiRequest("/employees", {
        accessToken,
      });

      const employeeRecords = Array.isArray(response)
        ? response
        : response?.employees || response?.data || [];

      const mappedEmployees = employeeRecords.map((employee) => {
        const primaryAssignment = employee.assignments?.[0];

        return {
          id: employee.employeeCode,
          employeeId: employee.id,

          name: employee.fullName,
          nickname: employee.nickname || "",
          email: employee.user?.email || "—",
          phone: employee.user?.phone || "—",

          department: primaryAssignment?.department?.name || "Unassigned",
          designation: primaryAssignment?.designation?.name || "Unassigned",
          branch: primaryAssignment?.branch?.name || "Unassigned",

          status: displayStatus[employee.status] || employee.status,
          joiningDate: employee.joiningDate,
          employmentType: primaryAssignment?.employmentType || "—",
          gender: employee.gender || '', salaryType: employee.compensation?.salaryType || '',
          ctc: employee.compensation?.ctc ?? null,
          paymentMode: employee.compensation?.paymentMode || '', shiftType: employee.workSchedule?.shiftType || '',
          accountStatus: employee.user?.status || 'NO_ACCOUNT',
          roles: (employee.user?.roles || []).map(value => value.role?.name).filter(Boolean),
          hasBankDetails: Boolean(
            employee.bankAccount?.accountNumberEncrypted &&
              employee.bankAccount?.ifscCodeEncrypted,
          ),
          hasAadhaar: (employee.documents || []).some(
            (document) =>
              document.documentType === "AADHAAR" &&
              document.documentNumberEncrypted,
          ),
          hasPan: (employee.documents || []).some(
            (document) =>
              document.documentType === "PAN" && document.documentNumberEncrypted,
          ),
          hasErpAccess: (employee.user?.roles || []).some(
            ({ role }) => role?.code && role.code !== "EMPLOYEE_SELF_SERVICE",
          ),
        };
      });

      setEmployees(mappedEmployees);
    } catch (error) {
      setLoadError(
        error.message || "Unable to load employees. Please try again.",
      );
      setEmployees([]);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const confirmDelete = async () => {
    if (!accessToken || !employeeToDelete) return;

    try {
      setIsDeleting(true);
      setLoadError("");

      await apiRequest(`/employees/${employeeToDelete.employeeId}`, {
        method: "DELETE",
        accessToken,
      });

      setEmployeeToDelete(null);
      await loadEmployees();
    } catch (error) {
      setLoadError(error.message || "Unable to remove employee.");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return employees.filter((employee) => {
      const searchableText = [
        employee.name,
        employee.nickname,
        employee.id,
        employee.email,
        employee.phone,
        employee.department,
        employee.designation,
        employee.branch,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !normalizedSearch || searchableText.includes(normalizedSearch);

      const matchesDepartment =
        department === "All" || employee.department === department;

      const matchesBranch = branch === "All" || employee.branch === branch;

      const matchesStatus = status === "All" || employee.status === status;

      const matchesBankDetails =
        bankDetails === "All" ||
        (bankDetails === "Complete" && employee.hasBankDetails) ||
        (bankDetails === "Missing" && !employee.hasBankDetails);

      const hasCompleteIdentity = employee.hasAadhaar && employee.hasPan;
      const matchesIdentityDetails =
        identityDetails === "All" ||
        (identityDetails === "Complete" && hasCompleteIdentity) ||
        (identityDetails === "Missing" && !hasCompleteIdentity) ||
        (identityDetails === "Missing Aadhaar" && !employee.hasAadhaar) ||
        (identityDetails === "Missing PAN" && !employee.hasPan);

      const matchesSystemAccess =
        systemAccess === "All" ||
        (systemAccess === "ERP Access" && employee.hasErpAccess) ||
        (systemAccess === "Self-service only" && !employee.hasErpAccess);

      return (
        matchesSearch &&
        matchesDepartment &&
        matchesBranch &&
        matchesStatus &&
        matchesBankDetails &&
        matchesIdentityDetails &&
        matchesSystemAccess
      );
    }).sort((a, b) => {
      const rank = employee => employee.status === 'Active' ? 0 : employee.status === 'Inactive' ? 2 : 1;
      return rank(a) - rank(b);
    });
  }, [
    employees,
    searchTerm,
    department,
    branch,
    status,
    bankDetails,
    identityDetails,
    systemAccess,
  ]);

  const departmentOptions = useMemo(
    () => [
      "All",
      ...Array.from(
        new Set(
          employees
            .map((employee) => employee.department)
            .filter(
              (departmentName) =>
                departmentName && departmentName !== "Unassigned",
            ),
        ),
      ).sort(),
    ],
    [employees],
  );

  const branchOptions = useMemo(
    () => [
      "All",
      ...Array.from(
        new Set(
          employees
            .map((employee) => employee.branch)
            .filter((branchName) => branchName && branchName !== "Unassigned"),
        ),
      ).sort(),
    ],
    [employees],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    department,
    branch,
    status,
    bankDetails,
    identityDetails,
    systemAccess,
  ]);

  useEffect(() => {
    if (!branchOptions.includes(branch)) {
      setBranch("All");
    }
  }, [branch, branchOptions]);

  useEffect(() => {
    if (!departmentOptions.includes(department)) {
      setDepartment("All");
    }
  }, [department, departmentOptions]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredEmployees.length / ITEMS_PER_PAGE),
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedEmployees = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;

    return filteredEmployees.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredEmployees, currentPage]);

  return (
    <div>
      <label className="mb-4 inline-flex cursor-pointer rounded-xl border border-primary-200 bg-white px-5 py-3 text-primary-800 font-semibold">
        {importBusy ? 'Reading file…' : 'Import inactive employees'}
        <input type="file" accept=".xlsx,.xls" className="hidden" disabled={importBusy} onChange={event => { importInactive(event.target.files?.[0]); event.target.value = ''; }} />
      </label>
      {inactivePreview && <div className="fixed inset-0 z-[100] bg-black/50 p-6 flex items-center justify-center"><div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-auto">
        <h2 className="text-xl font-bold mb-3">Preview inactive employees</h2>
        <p className="mb-3">Only missing employees listed below will be created as inactive.</p>
        <table className="w-full border text-sm"><thead className="bg-primary-800 text-white"><tr><th className="p-3">Name</th><th>Old code</th><th>Action</th></tr></thead><tbody>{inactivePreview.rows.map(row => <tr key={row.employeeCode} className="border-t"><td className="p-3">{row.fullName}</td><td>{row.employeeCode}</td><td>{row.error || row.action}</td></tr>)}</tbody></table>
        <div className="mt-4 flex justify-end gap-3"><button disabled={importBusy} onClick={() => setInactivePreview(null)} className="border rounded-xl px-4 py-2">Cancel</button><button disabled={importBusy || inactivePreview.rows.some(row => row.error)} onClick={() => importInactive(inactiveFile, true)} className="bg-primary-800 text-white rounded-xl px-4 py-2">{importBusy ? 'Importing…' : 'Submit import'}</button></div>
      </div></div>}
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Employees</h1>

          <p className="mt-2 text-gray-500">
            Manage employees across all branches.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/admindashboard/employees/add")}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-800 px-5 py-3 font-medium text-white shadow-sm transition-all hover:bg-red-900"
        >
          <FiPlus size={18} />
          Add Employee
        </button>
      </div>

      {loadError ? (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {loadError}
        </div>
      ) : null}

      <EmployeeFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        branch={branch}
        setBranch={setBranch}
        department={department}
        setDepartment={setDepartment}
        status={status}
        setStatus={setStatus}
        bankDetails={bankDetails}
        setBankDetails={setBankDetails}
        identityDetails={identityDetails}
        setIdentityDetails={setIdentityDetails}
        systemAccess={systemAccess}
        setSystemAccess={setSystemAccess}
        branches={branchOptions}
        departments={departmentOptions}
      />

      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing{" "}
          <span className="font-semibold text-gray-900">
            {filteredEmployees.length}
          </span>{" "}
          employee{filteredEmployees.length === 1 ? "" : "s"}
        </p>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-100 bg-white py-16 text-center text-gray-500">
          Loading employees...
        </div>
      ) : (
        <>
          <EmployeeTable
            employees={paginatedEmployees}
            onDeleteEmployee={setEmployeeToDelete}
            onActivateEmployee={async employee => {
              if (!window.confirm('Activate ' + employee.name + ' with the next C-code and default login?')) return;
              try {
                await apiRequest('/employees/' + employee.employeeId + '/reactivate', { method: 'POST', accessToken });
                await loadEmployees();
              } catch (error) { setLoadError(error.message); }
            }}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredEmployees.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {employeeToDelete ? (
        <DeleteEmployeeModal
          employee={employeeToDelete}
          isDeleting={isDeleting}
          onClose={() => {
            if (!isDeleting) {
              setEmployeeToDelete(null);
            }
          }}
          onConfirm={confirmDelete}
        />
      ) : null}
    </div>
  );
}

function DeleteEmployeeModal({ employee, isDeleting, onClose, onConfirm }) {
  const displayName = employee.nickname?.trim() || employee.name;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-600">
            <FiAlertTriangle size={24} />
          </div>

          <button
            type="button"
            disabled={isDeleting}
            onClick={onClose}
            className="rounded-xl p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
            aria-label="Close"
          >
            <FiX size={20} />
          </button>
        </div>

        <h2 className="mt-6 text-xl font-bold text-gray-900">
          Remove employee?
        </h2>

        <p className="mt-3 text-sm leading-6 text-gray-600">
          <span className="font-semibold text-gray-900">
            {displayName} ({employee.id})
          </span>{" "}
          will no longer appear in active employee lists and their login access
          will be disabled.
        </p>

        <p className="mt-3 text-sm leading-6 text-gray-500">
          Existing payroll, attendance, sales, commissions, and audit records
          will remain safely preserved.
        </p>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            disabled={isDeleting}
            onClick={onClose}
            className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={isDeleting}
            onClick={onConfirm}
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            <FiTrash2 />
            {isDeleting ? "Removing..." : "Remove Employee"}
          </button>
        </div>
      </div>
    </div>
  );
}
